import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Settings, updateSettings } from '../../api/settings';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

interface SettingsFormProps {
  initial?: Settings;
  onUpdate?: () => void;
}

const SettingsForm = ({ initial, onUpdate }: SettingsFormProps) => {
  const { token } = useAuth();
  const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm<Settings>({
    defaultValues: {
      platformName: 'Workloob',
      platformLogo: '',
      platformTagline: 'Build with confidence',
      primaryColor: '#6366f1',
      footerText: '',
      heroTitle: 'Build better workflows with Workloob',
      heroDescription: 'Explore APIs, SDKs, and operational guides for everything from onboarding to advanced automation inside the Workloob platform.',
      primaryButtonText: 'Launch Dashboard',
      primaryButtonLink: 'https://app.workloob.com',
      secondaryButtonText: 'How to work',
      secondaryButtonLink: ''
    }
  });

  useEffect(() => {
    if (initial) {
      reset(initial);
    }
  }, [initial, reset]);

  const onSubmit = async (values: Settings) => {
    if (!token) {
      toast.error('Not authenticated');
      return;
    }

    try {
      // Remove _id and other non-schema fields before sending
      const { _id, ...settingsData } = values;
      // TypeScript-safe way to exclude optional fields
      const cleanData: Partial<Settings> = {
        platformName: settingsData.platformName,
        platformLogo: settingsData.platformLogo,
        platformTagline: settingsData.platformTagline,
        primaryColor: settingsData.primaryColor,
        footerText: settingsData.footerText,
        heroTitle: settingsData.heroTitle,
        heroDescription: settingsData.heroDescription,
        primaryButtonText: settingsData.primaryButtonText,
        primaryButtonLink: settingsData.primaryButtonLink,
        secondaryButtonText: settingsData.secondaryButtonText,
        secondaryButtonLink: settingsData.secondaryButtonLink
      };
      await updateSettings(cleanData, token);
      toast.success('Settings updated');
      onUpdate?.();
    } catch (error) {
      console.error('Settings update error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update settings');
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-6 rounded-2xl border border-slate-200 bg-white/80 p-6 shadow-card dark:border-slate-800 dark:bg-slate-900/60"
    >
      <div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">Platform Branding</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">Customize how your documentation appears to users</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
          Platform Name
          <input
            {...register('platformName')}
            placeholder="Workloob"
            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 dark:border-slate-700 dark:bg-slate-900"
          />
        </label>
        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
          Primary Color (hex)
          <input
            type="color"
            {...register('primaryColor')}
            className="mt-2 h-10 w-full rounded-xl border border-slate-200 dark:border-slate-700"
          />
        </label>
        <label className="text-sm font-medium text-slate-600 dark:text-slate-300 md:col-span-2">
          Platform Logo URL
          <input
            {...register('platformLogo')}
            type="url"
            placeholder="https://example.com/logo.png"
            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 dark:border-slate-700 dark:bg-slate-900"
          />
        </label>
        <label className="text-sm font-medium text-slate-600 dark:text-slate-300 md:col-span-2">
          Platform Tagline
          <input
            {...register('platformTagline')}
            placeholder="Build with confidence"
            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 dark:border-slate-700 dark:bg-slate-900"
          />
        </label>
        <label className="text-sm font-medium text-slate-600 dark:text-slate-300 md:col-span-2">
          Footer Text
          <textarea
            {...register('footerText')}
            rows={3}
            placeholder="Optional footer text"
            className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900"
          />
        </label>
      </div>

      <div className="border-t border-slate-200 pt-6 dark:border-slate-800">
        <div className="mb-4">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">Landing Page</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">Customize the home page hero section</p>
        </div>
        
        <div className="grid gap-4 md:grid-cols-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300 md:col-span-2">
            Hero Title
            <input
              {...register('heroTitle')}
              placeholder="Build better workflows with Workloob"
              className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 dark:border-slate-700 dark:bg-slate-900"
            />
          </label>
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300 md:col-span-2">
            Hero Description
            <textarea
              {...register('heroDescription')}
              rows={3}
              placeholder="Explore APIs, SDKs, and operational guides..."
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900"
            />
          </label>
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
            Primary Button Text
            <input
              {...register('primaryButtonText')}
              placeholder="Launch Dashboard"
              className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 dark:border-slate-700 dark:bg-slate-900"
            />
          </label>
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
            Primary Button Link
            <input
              {...register('primaryButtonLink')}
              type="url"
              placeholder="https://app.workloob.com"
              className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 dark:border-slate-700 dark:bg-slate-900"
            />
          </label>
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
            Secondary Button Text
            <input
              {...register('secondaryButtonText')}
              placeholder="How to work"
              className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 dark:border-slate-700 dark:bg-slate-900"
            />
          </label>
          <label className="text-sm font-medium text-slate-600 dark:text-slate-300">
            Secondary Button Link
            <input
              {...register('secondaryButtonLink')}
              type="url"
              placeholder="Leave empty to link to first doc"
              className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 dark:border-slate-700 dark:bg-slate-900"
            />
          </label>
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full rounded-2xl bg-brand-600 py-3 text-center text-lg font-semibold text-white transition hover:bg-brand-500 disabled:cursor-not-allowed disabled:bg-slate-400"
      >
        {isSubmitting ? 'Saving...' : 'Save Settings'}
      </button>
    </form>
  );
};

export default SettingsForm;

